// src/utils/slotMath.js

export function classifyTimeOfDay(hour) {
  if (hour < 7) return 'night';
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

/**
 * Build the 24 hourly slots for a single day.
 *
 * @param {DateTime} date - any Luxon DateTime within the target day; we
 *   anchor to its own start-of-day, so callers may pass a pre-anchored
 *   `dayStartHome` or any time within the day.
 * @param {string} timezone - IANA zone name to display the slots in.
 * @param {boolean} [use24HourFormat=false] - render labels as `HH:mm` /
 *   `HH` instead of 12-hour with AM/PM.
 * @returns {Array<{hour, localHour, timeOfDay, label, isMidnight, dateTime}>}
 */
export function buildHourList(date, timezone, use24HourFormat = false) {
  const dayStartHome = date.startOf('day');
  const slots = [];
  for (let i = 0; i < 24; i++) {
    const slot = dayStartHome.plus({ hours: i }).setZone(timezone);
    const localHour = slot.hour;
    const localMinute = slot.minute;

    let timeStr;
    let isMidnight = false;
    if (localHour === 0 && localMinute === 0) {
      timeStr = `${slot.toFormat('MMM')}\n${slot.toFormat('d')}`;
      isMidnight = true;
    } else if (use24HourFormat) {
      timeStr = localMinute !== 0
        ? `${slot.toFormat('HH')}:${slot.toFormat('mm')}`
        : slot.toFormat('HH');
    } else {
      const hour12 = localHour % 12 || 12;
      const period = localHour >= 12 ? 'PM' : 'AM';
      timeStr = localMinute !== 0
        ? `${hour12}:${slot.toFormat('mm')}\n${period}`
        : `${hour12}\n${period}`;
    }

    slots.push({
      hour: i,
      localHour,
      timeOfDay: classifyTimeOfDay(localHour),
      label: timeStr,
      isMidnight,
      dateTime: slot,
    });
  }
  return slots;
}

/**
 * Format a fractional hour value (e.g. 14.5) as a 12-hour clock string
 * with AM/PM and an optional minutes suffix.
 *
 * Used in event tooltips to label start/end of a meeting that may span
 * partial hours.
 */
export function formatHour(h) {
  const wrapped = ((h % 24) + 24) % 24;
  const whole = Math.floor(wrapped);
  const minutes = Math.round((wrapped - whole) * 60);
  const hour12 = whole % 12 || 12;
  const period = whole >= 12 ? 'PM' : 'AM';
  return minutes
    ? `${hour12}:${String(minutes).padStart(2, '0')} ${period}`
    : `${hour12}:00 ${period}`;
}
